import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { QueryStatus } from '@tanstack/react-query';
import { ListFilterIcon, PanelLeftIcon, SquarePenIcon } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_NEW_MESSAGE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ShortcutHint, useShortcutLabel } from '@/components/ui/kbd';
import { SHORTCUTS } from '@/constants/shortcuts';
import { useDeskId } from '@/hooks/useDeskId';
import { usePleaseStay } from '@/hooks/usePleaseStay';
import { cn } from '@/lib/utils';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectIframeView } from '@/redux/user/selectors';
import { trackEvent } from '@/segment/segment';
import { Conversation } from '@/shared/types/chat';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import { ResizeHandle } from './ResizeHandle';
import { useResizablePanel } from './useResizablePanel';
import { useConversations } from '../../hooks/useConversations';
import { useMessageList } from '../../hooks/useMessageList';
import { setSideBarOpen, setView } from '../../redux/actions';
import {
  selectSideBarOpen,
  selectViewSelectedStatus,
  selectViewSelectedAgent,
  selectViewSelectedDepartment,
  selectViewSelectedBrain,
  selectViewSelectedSort,
  selectViewSelectedView,
} from '../../redux/selectors';

const MIN_WIDTH = 240;
const MAX_WIDTH = 512;
const DEFAULT_WIDTH = 300;
const DEFAULT_WIDTH_3XL = 448;
const BREAKPOINT_LG = 1024;
const BREAKPOINT_3XL = 1920;
const STORAGE_KEY = 'humanChat.messageList.width';

const getDefaultWidth = () => {
  if (window.innerWidth >= BREAKPOINT_3XL) {
    return DEFAULT_WIDTH_3XL;
  }
  if (window.innerWidth < BREAKPOINT_LG) {
    return MIN_WIDTH;
  }
  return DEFAULT_WIDTH;
};

// Must be a stable callback, or `ConversationsData` re-renders with its parent for nothing.
export type ConversationsRenderProp = (props: {
  conversations: Conversation[];
  hasNextPage: boolean;
  listStatus: QueryStatus;
  fetchNextPage: () => void;
}) => React.ReactNode;

interface MessageListContainerProps {
  children: ConversationsRenderProp;
}

const ConversationsData = memo(({ children }: { children: ConversationsRenderProp }) => {
  const deskId = useDeskId();
  const selectedStatus = useSelector(selectViewSelectedStatus);
  const selectedAgent = useSelector(selectViewSelectedAgent);
  const selectedDepartment = useSelector(selectViewSelectedDepartment);
  const selectedBrain = useSelector(selectViewSelectedBrain);
  const selectedSort = useSelector(selectViewSelectedSort);
  const selectedView = useSelector(selectViewSelectedView);

  // No `sessionId`: the list has no use for a conversation-detail query to re-render on.
  const { conversations, hasNextPage, listStatus, fetchNextPage, unassignedCount } =
    useConversations({
      deskId,
      selectedAgent,
      selectedDepartment,
      selectedBrain,
      selectedSort,
      selectedStatus,
      selectedView,
    });
  usePleaseStay(!!unassignedCount);

  return children({ conversations, hasNextPage, listStatus, fetchNextPage });
});

ConversationsData.displayName = 'ConversationsData';

export const MessageListContainer = ({ children }: MessageListContainerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );
  const showLeftSidebar = useSelector(selectSideBarOpen);
  const setShowLeftSidebar = useCallback(() => {
    dispatch(setSideBarOpen(!showLeftSidebar));
  }, [dispatch, showLeftSidebar]);

  const { title } = useMessageList();
  const titleRef = useRef<HTMLSpanElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const {
    width: panelWidth,
    setWidth: setPanelWidth,
    panelRef,
    isActive,
    hasUserResized,
    wasRestoredFromStorage,
    separatorProps,
  } = useResizablePanel({
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    initialWidth: getDefaultWidth(),
    storageKey: STORAGE_KEY,
  });

  // Follow breakpoint defaults until the user manually resizes or a saved width is restored,
  // then respect that width. A restored width is intentionally kept across breakpoints; it is
  // only bounded by MAX_WIDTH (not the viewport), which is acceptable for this desktop console.
  useEffect(() => {
    if (hasUserResized || wasRestoredFromStorage) {
      return;
    }
    const mq2xl = window.matchMedia(`(min-width: ${BREAKPOINT_3XL}px)`);
    const mqLg = window.matchMedia(`(min-width: ${BREAKPOINT_LG}px)`);
    const handler = () => setPanelWidth(getDefaultWidth());
    mq2xl.addEventListener('change', handler);
    mqLg.addEventListener('change', handler);
    return () => {
      mq2xl.removeEventListener('change', handler);
      mqLg.removeEventListener('change', handler);
    };
  }, [setPanelWidth, hasUserResized, wasRestoredFromStorage]);

  useEffect(() => {
    const el = titleRef.current;
    if (el) {
      setIsTitleTruncated(el.scrollWidth > el.offsetWidth);
    }
  }, [title, panelWidth]);

  const selectedStatus = useSelector(selectViewSelectedStatus);
  const selectedSort = useSelector(selectViewSelectedSort);
  const selectedView = useSelector(selectViewSelectedView);

  const isUsingView = Boolean(selectedView);

  const sidebarShortcutLabel = useShortcutLabel(SHORTCUTS.toggleSidebar);

  useHotkeys(
    SHORTCUTS.toggleSidebar,
    setShowLeftSidebar,
    { enabled: true, enableOnFormTags: false },
    [setShowLeftSidebar]
  );

  const isSpamOrTrash = selectedStatus === 'spam' || selectedStatus === 'trash';

  const handleSortChange = useCallback(
    (value: string) => {
      dispatch(setView({ selectedSort: value as 'newest' | 'oldest' }));
    },
    [dispatch]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      dispatch(setView({ selectedStatus: value as 'open' | 'closed' }));
    },
    [dispatch]
  );

  const openNewMessageModal = useCallback(() => {
    trackEvent(EventName.ClickNewMessageSquarePen);
    dispatch(pushModal(MODAL_NEW_MESSAGE, {}));
  }, [dispatch]);

  const isIframeView = useSelector(selectIframeView);

  return (
    <div ref={panelRef} style={{ width: panelWidth }} className="relative z-[2] bg-subtle">
      <div
        className={cn(
          'flex h-14 items-center gap-1 border-b border-border bg-background pl-2.5',
          showLeftSidebar && 'open'
        )}
      >
        {!isIframeView && (
          <InfoTooltip
            content={
              <span className="inline-flex items-center gap-1.5">
                {t('conversation.toggle_sidebar_label')}{' '}
                <ShortcutHint shortcut={SHORTCUTS.toggleSidebar} />
              </span>
            }
            showArrow
          >
            <Button
              variant="ghost-neutral"
              size="icon-sm"
              className="px-2"
              aria-label={t('conversation.toggle_sidebar', { 0: sidebarShortcutLabel })}
              onClick={setShowLeftSidebar}
            >
              <PanelLeftIcon size={18} className="text-muted-foreground" />
            </Button>
          </InfoTooltip>
        )}

        <InfoTooltip content={isTitleTruncated ? (title as string) : undefined}>
          <span ref={titleRef} className="max-w-50 truncate text-sm font-semibold text-foreground">
            {title as string}
          </span>
        </InfoTooltip>

        <DropdownMenu>
          <InfoTooltip content={t('conversation.display_options')}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost-neutral"
                size="icon-sm"
                className="ml-auto px-2"
                aria-label={t('conversation.display_options')}
              >
                <ListFilterIcon size={18} className="text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
          </InfoTooltip>
          <DropdownMenuContent align="end" className="w-52">
            {!isSpamOrTrash && !isUsingView && (
              <>
                <DropdownMenuLabel className="text-muted-foreground">
                  {t('common.show')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={selectedStatus} onValueChange={handleStatusChange}>
                  <DropdownMenuRadioItem value="open">
                    {t('conversation.open_conversations')}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="closed">
                    {t('conversation.closed_conversations')}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuLabel className="text-muted-foreground">
              {t('conversation.sort_by')}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup value={selectedSort} onValueChange={handleSortChange}>
              <DropdownMenuRadioItem value="newest">
                {t('conversation.recent_first')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="oldest">
                {t('conversation.oldest_first')}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {canWrite && (
          <InfoTooltip content={t('conversation.new_message.title')}>
            <Button
              variant="ghost-neutral"
              size="icon-sm"
              className="mr-2 px-2"
              onClick={openNewMessageModal}
              aria-label={t('conversation.new_message.title')}
            >
              <SquarePenIcon size={18} className="text-muted-foreground" />
            </Button>
          </InfoTooltip>
        )}
      </div>
      <div className="px-2 pt-2 pb-2">
        <ConversationsData>{children}</ConversationsData>
      </div>
      <ResizeHandle
        separatorProps={separatorProps}
        isActive={isActive}
        label={t('conversation.resize_panel')}
      />
    </div>
  );
};
